import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Trophy, ArrowLeft, Paperclip, Upload, Download, Trash2, ChevronDown, ChevronUp, Loader, Wallet,
  AlertTriangle, Layers, Search, CheckCircle, XCircle,
} from 'lucide-react';
import Header from '../components/Header';
import FestivalExpensesCard from '../components/FestivalExpensesCard';
import { prepareImageForUpload } from '../utils/imageUpload';
import AttachmentViewer from '../components/AttachmentViewer';
import { participationsAPI, tournamentSeriesAPI, festivalExpensesAPI, handleApiError } from '../utils/api';

const STATUS_LABELS = {
  REGISTERED: { label: 'Inscrit', className: 'bg-blue-100 text-blue-800' },
  CONFIRMED: { label: 'Confirmé', className: 'bg-green-100 text-green-800' },
  CANCELLED: { label: 'Annulé', className: 'bg-gray-200 text-gray-600' },
};

const formatEuros = (value) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value || 0);

// "Buy-in payé" = entrées x buy-in du tournoi (Tournament.buyInAmount, V22),
// quand les deux sont connus. Renvoie null sinon (champ alors libre).
const computeBuyInPaid = (buyInAmount, entriesCount) => {
  const entries = parseInt(entriesCount, 10);
  if (buyInAmount == null || !Number.isFinite(entries) || entries < 1) return null;
  return Math.round(Number(buyInAmount) * entries * 100) / 100;
};

const PAGE_SIZE = 15;

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const dateOf = (p) => String(p.tournamentDate || '').slice(0, 10);
const formatDay = (str) =>
  str ? new Date(`${str}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

// Un résultat est "saisi" dès qu'une position ou un gain (même 0 €) existe.
const groupHasResult = (group, activeMembers) =>
  group.hasSeries
    ? activeMembers[0]?.seriesFinishPosition != null || activeMembers[0]?.seriesPrizeWon != null
    : activeMembers.some((m) => m.finishPosition != null || m.prizeWon != null);

// todo = terminé sans résultat ; upcoming = pas encore fini (y compris en cours) ;
// done = terminé avec résultat ; cancelled = toutes les inscriptions annulées.
const classifyGroup = (group, today) => {
  const active = group.members.filter((m) => m.status !== 'CANCELLED');
  if (active.length === 0) return 'cancelled';
  const last = active.map(dateOf).filter(Boolean).sort().pop();
  if (!last || last >= today) return 'upcoming';
  return groupHasResult(group, active) ? 'done' : 'todo';
};

const TABS = [
  { id: 'todo', label: 'À saisir' },
  { id: 'upcoming', label: 'À venir' },
  { id: 'done', label: 'Terminées' },
  { id: 'all', label: 'Toutes' },
];

/**
 * Regroupe les participations appartenant à une même série de tournois
 * (structures multi-jours : Day 1A / Day 1B / Final Day), reliées côté
 * admin via Tournament.series (voir TournamentSeries). La série a son
 * propre nom (ex: "PokerStars Open Main Event"), indépendant du nom de
 * chaque jour, donc pas de risque de répéter le titre d'une étape comme
 * titre du groupe.
 *
 * Un groupe n'est affiché comme "série" que s'il contient plus d'une
 * participation : un tournoi seul rattaché à une série où l'utilisateur
 * n'a qu'une inscription n'a rien à regrouper visuellement.
 */
const buildGroups = (list) => {
  const map = new Map();
  list.forEach((p) => {
    const key = p.seriesId || `standalone-${p.id}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  });

  return Array.from(map.entries()).map(([key, members]) => {
    const title = members[0].seriesName || members[0].tournamentTitle;
    return {
      key,
      title,
      festivalTitle: members[0].festivalTitle,
      members,
      // Un tournoi rattaché à une série (même un seul day inscrit pour
      // l'instant) doit passer par renderSeries : c'est là, et uniquement
      // là, que la position finale et le cashout peuvent être saisis
      // depuis le passage au résultat de série (voir SeriesResult côté
      // backend) - contrairement à `isSeries` ci-dessous qui ne sert qu'à
      // afficher ou non le badge "Série multi-jours".
      hasSeries: !!members[0].seriesId,
      isSeries: members.length > 1,
    };
  });
};

/**
 * Page "Mes participations" : liste les tournois auxquels le joueur est
 * inscrit, et lui permet de saisir lui-même son résultat (position, gain,
 * nombre d'entrées) une fois le tournoi joué, ainsi que d'attacher des
 * justificatifs de frais (photos/PDF).
 *
 * Les tournois faisant partie d'une même série (rattachés via
 * Tournament.series côté admin) sont regroupés sous une seule carte, avec
 * un bilan financier calculé une fois pour tout le groupe plutôt que par
 * étape (voir buildGroups).
 */
const MyParticipationsPage = () => {
  const navigate = useNavigate();

  const [participations, setParticipations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [resultDrafts, setResultDrafts] = useState({});
  // Participations dont le "Buy-in payé" est saisi à la main (lien "Modifier")
  // au lieu d'être calculé automatiquement.
  const [manualBuyInIds, setManualBuyInIds] = useState(new Set());
  const [busyId, setBusyId] = useState(null);

  // Résultat final de la SÉRIE entière (position + cashout), distinct du
  // résultat par day ci-dessus : une série n'a qu'un seul résultat, celui
  // du jour où le joueur est éliminé (ou du dernier day atteint) - voir
  // seriesFinishPosition/seriesPrizeWon sur chaque participation membre.
  const [seriesResultDrafts, setSeriesResultDrafts] = useState({});
  const [busySeriesId, setBusySeriesId] = useState(null);

  const [attachments, setAttachments] = useState({}); // { [participationId]: ParticipationAttachmentDTO[] }
  const [attachmentsLoading, setAttachmentsLoading] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const [pendingAmounts, setPendingAmounts] = useState({}); // { [participationId]: '12.50' }

  // Navigation dans une longue liste : onglet, recherche, année, pagination.
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [year, setYear] = useState('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Groupes dont le résultat vient d'être saisi : ils restent visibles dans
  // "À saisir" jusqu'au changement d'onglet, pour pouvoir compléter (frais...).
  const [pinnedKeys, setPinnedKeys] = useState(new Set());
  const tabInitialized = useRef(false);
  const [festivalExpenses, setFestivalExpenses] = useState([]);
  const [viewing, setViewing] = useState(null); // justificatif ouvert dans la visionneuse

  const loadParticipations = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const response = await participationsAPI.getMine();
      const list = response.data || [];
      setParticipations(list);
      setResultDrafts(
        list.reduce((acc, p) => {
          acc[p.id] = {
            finishPosition: p.finishPosition ?? '',
            prizeWon: p.prizeWon ?? '',
            entriesCount: p.entriesCount ?? '',
            buyInPaid: p.buyInPaid ?? '',
            totalEntries: p.totalEntries ?? '',
          };
          return acc;
        }, {})
      );
      // Une valeur déjà enregistrée qui diffère du calcul (tarif réduit,
      // satellite...) reste en saisie manuelle : on ne l'écrase pas.
      setManualBuyInIds(
        new Set(
          list
            .filter((p) => {
              const auto = computeBuyInPaid(p.tournamentBuyInAmount, p.entriesCount);
              return auto != null && p.buyInPaid != null && Math.abs(Number(p.buyInPaid) - auto) > 0.005;
            })
            .map((p) => p.id)
        )
      );
      setSeriesResultDrafts(
        list.reduce((acc, p) => {
          if (p.seriesId && !acc[p.seriesId]) {
            acc[p.seriesId] = {
              finishPosition: p.seriesFinishPosition ?? '',
              prizeWon: p.seriesPrizeWon ?? '',
              totalEntries: p.seriesTotalEntries ?? '',
            };
          }
          return acc;
        }, {})
      );
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setError(`${errTitle} : ${details}`);
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadParticipations();
  }, [loadParticipations]);

  // Frais de festival : chargés à part, une erreur ici ne bloque pas la page.
  useEffect(() => {
    festivalExpensesAPI
      .getMine()
      .then((response) => setFestivalExpenses(response.data || []))
      .catch((err) => console.error('Erreur chargement frais de festival', err));
  }, []);

  const loadAttachments = async (participationId) => {
    setAttachmentsLoading((prev) => ({ ...prev, [participationId]: true }));
    try {
      const response = await participationsAPI.getAttachments(participationId);
      setAttachments((prev) => ({ ...prev, [participationId]: response.data || [] }));
    } catch (err) {
      console.error('Erreur chargement pièces jointes', err);
    } finally {
      setAttachmentsLoading((prev) => ({ ...prev, [participationId]: false }));
    }
  };

  const toggleExpand = (participation) => {
    const next = expandedId === participation.id ? null : participation.id;
    setExpandedId(next);
    if (next && !attachments[participation.id]) {
      loadAttachments(participation.id);
    }
  };

  const handleDraftChange = (participationId, field, value) => {
    setResultDrafts((prev) => ({
      ...prev,
      [participationId]: { ...prev[participationId], [field]: value },
    }));
  };

  const handleSaveResult = async (participation, overrides = {}) => {
    const draft = { ...(resultDrafts[participation.id] || {}), ...overrides };
    const autoBuyIn = manualBuyInIds.has(participation.id)
      ? null
      : computeBuyInPaid(participation.tournamentBuyInAmount, draft.entriesCount);
    setBusyId(participation.id);
    try {
      const response = await participationsAPI.setMyResult(participation.id, {
        finishPosition: draft.finishPosition === '' ? null : parseInt(draft.finishPosition, 10),
        prizeWon: draft.prizeWon === '' ? null : parseFloat(draft.prizeWon),
        entriesCount: draft.entriesCount === '' ? null : parseInt(draft.entriesCount, 10),
        buyInPaid: autoBuyIn != null ? autoBuyIn : (draft.buyInPaid === '' ? null : parseFloat(draft.buyInPaid)),
        totalEntries: draft.totalEntries === '' || draft.totalEntries == null ? null : parseInt(draft.totalEntries, 10),
      });
      setParticipations((prev) => prev.map((p) => (p.id === participation.id ? response.data : p)));
      pinGroup(participation.seriesId || `standalone-${participation.id}`);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible d'enregistrer le résultat : ${errTitle} - ${details}`);
    } finally {
      setBusyId(null);
    }
  };

  const pinGroup = (key) => setPinnedKeys((prev) => new Set(prev).add(key));

  const handleSeriesDraftChange = (seriesId, field, value) => {
    setSeriesResultDrafts((prev) => ({
      ...prev,
      [seriesId]: { ...prev[seriesId], [field]: value },
    }));
  };

  const handleSaveSeriesResult = async (group) => {
    const draft = seriesResultDrafts[group.key] || {};
    const festivalId = group.members[0].festivalId;
    setBusySeriesId(group.key);
    try {
      await tournamentSeriesAPI.setMyResult(festivalId, group.key, {
        finishPosition: draft.finishPosition === '' ? null : parseInt(draft.finishPosition, 10),
        prizeWon: draft.prizeWon === '' ? null : parseFloat(draft.prizeWon),
        totalEntries: draft.totalEntries === '' ? null : parseInt(draft.totalEntries, 10),
      });
      pinGroup(group.key);
      await loadParticipations(true);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible d'enregistrer le résultat de la série : ${errTitle} - ${details}`);
    } finally {
      setBusySeriesId(null);
    }
  };

  // Saisie rapide : "Sans gain" enregistre un résultat à 0 € (sorti hors de
  // l'argent), ce qui retire le tournoi de la liste "À saisir".
  const handleQuickNoCash = async (group) => {
    if (group.hasSeries) {
      const draft = seriesResultDrafts[group.key] || {};
      setBusySeriesId(group.key);
      try {
        await tournamentSeriesAPI.setMyResult(group.members[0].festivalId, group.key, {
          finishPosition: null,
          prizeWon: 0,
          totalEntries: draft.totalEntries === '' || draft.totalEntries == null ? null : parseInt(draft.totalEntries, 10),
        });
        await loadParticipations(true);
      } catch (err) {
        const { error: errTitle, details } = handleApiError(err);
        alert(`Impossible d'enregistrer le résultat : ${errTitle} - ${details}`);
      } finally {
        setBusySeriesId(null);
      }
    } else {
      const participation = group.members[0];
      handleDraftChange(participation.id, 'finishPosition', '');
      handleDraftChange(participation.id, 'prizeWon', '0');
      await handleSaveResult(participation, { finishPosition: '', prizeWon: 0 });
    }
  };

  // "Pas joué" : annule l'inscription (les tournois annulés ne comptent plus).
  const handleQuickNotPlayed = async (group) => {
    if (!window.confirm(`Marquer "${group.title}" comme non joué ? L'inscription sera annulée.`)) return;
    setBusySeriesId(group.key);
    try {
      for (const m of group.members.filter((x) => x.status !== 'CANCELLED')) {
        await participationsAPI.cancel(m.id);
      }
      await loadParticipations(true);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible d'annuler : ${errTitle} - ${details}`);
    } finally {
      setBusySeriesId(null);
    }
  };

  const handleUpload = async (participationId, file) => {
    if (!file) return;
    setUploadingId(participationId);
    try {
      const amount = pendingAmounts[participationId];
      await participationsAPI.uploadAttachment(participationId, await prepareImageForUpload(file), amount || null);
      setPendingAmounts((prev) => ({ ...prev, [participationId]: '' }));
      await loadAttachments(participationId);
      await loadParticipations(true); // le total des frais (et donc le bilan) a changé
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible d'envoyer le fichier : ${errTitle} - ${details}`);
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteAttachment = async (participationId, attachmentId) => {
    if (!window.confirm('Supprimer cette pièce jointe ?')) return;
    try {
      await participationsAPI.deleteAttachment(attachmentId);
      await loadAttachments(participationId);
      await loadParticipations(true); // le total des frais (et donc le bilan) a changé
    } catch (err) {
      alert('Impossible de supprimer la pièce jointe');
    }
  };

  const groups = useMemo(() => buildGroups(participations), [participations]);

  // Groupes enrichis (catégorie, dates) pour filtrer et trier.
  const enrichedGroups = useMemo(() => {
    const today = todayStr();
    return groups.map((g) => {
      const dates = g.members.map(dateOf).filter(Boolean).sort();
      return {
        ...g,
        category: classifyGroup(g, today),
        firstDate: dates[0] || '',
        lastDate: dates[dates.length - 1] || '',
        searchText: [g.title, g.festivalTitle, ...g.members.map((m) => m.tournamentTitle)]
          .filter(Boolean).join(' ').toLowerCase(),
      };
    });
  }, [groups]);

  const counts = useMemo(
    () => enrichedGroups.reduce(
      (acc, g) => ({ ...acc, [g.category]: acc[g.category] + 1, all: acc.all + 1 }),
      { todo: 0, upcoming: 0, done: 0, cancelled: 0, all: 0 }
    ),
    [enrichedGroups]
  );

  const availableYears = useMemo(
    () => Array.from(new Set(enrichedGroups.map((g) => g.lastDate.slice(0, 4)).filter(Boolean))).sort().reverse(),
    [enrichedGroups]
  );

  // Au premier chargement : on ouvre "À saisir" s'il y a des résultats en attente.
  useEffect(() => {
    if (isLoading || tabInitialized.current) return;
    tabInitialized.current = true;
    setTab(counts.todo > 0 ? 'todo' : 'all');
  }, [isLoading, counts.todo]);

  // Changer de filtre repart de la première page et libère les groupes épinglés.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    setPinnedKeys(new Set());
  }, [tab, search, year]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = enrichedGroups.filter((g) => {
      const inTab =
        tab === 'all' ||
        g.category === tab ||
        (tab === 'todo' && g.category === 'done' && pinnedKeys.has(g.key));
      if (!inTab) return false;
      if (year !== 'all' && g.lastDate.slice(0, 4) !== year) return false;
      return !q || g.searchText.includes(q);
    });
    // À venir : le plus proche d'abord ; sinon le plus récent d'abord.
    return list.sort((a, b) =>
      tab === 'upcoming' ? a.firstDate.localeCompare(b.firstDate) : b.lastDate.localeCompare(a.lastDate)
    );
  }, [enrichedGroups, tab, search, year, pinnedKeys]);

  const visibleGroups = filteredGroups.slice(0, visibleCount);

  // Bandeau de saisie rapide pour un tournoi/une série terminé(e) sans résultat.
  const renderQuickStrip = (group) => {
    if (group.category !== 'todo') return null;
    const busy = busySeriesId === group.key || busyId === group.members[0].id;
    return (
      <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-amber-800 font-medium mr-auto flex items-center gap-1">
          <AlertTriangle size={14} /> Résultat à saisir
        </span>
        <button
          onClick={() => handleQuickNoCash(group)}
          disabled={busy}
          className="px-3 py-1 rounded-lg bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 disabled:opacity-50 flex items-center gap-1"
          title="Enregistre un résultat à 0 € (hors de l'argent)"
        >
          <CheckCircle size={14} /> Sans gain
        </button>
        <button
          onClick={() => handleQuickNotPlayed(group)}
          disabled={busy}
          className="px-3 py-1 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 flex items-center gap-1"
          title="Annule l'inscription"
        >
          <XCircle size={14} /> Pas joué
        </button>
      </div>
    );
  };

  // Bilan global : calculé à partir des groupes plutôt que des
  // participations à plat, pour ne compter le gain d'une série qu'une
  // seule fois (seriesPrizeWon) plutôt que de le sommer (à tort) sur
  // chaque day, qui n'a plus de prizeWon propre depuis le passage au
  // résultat de série (voir buildGroups / seriesFinishPosition).
  const activeParticipations = participations.filter((p) => p.status !== 'CANCELLED');
  const globalSummary = groups.reduce(
    (acc, group) => {
      const activeMembers = group.members.filter((m) => m.status !== 'CANCELLED');
      const legTotals = activeMembers.reduce(
        (sum, m) => ({
          buyInPaid: sum.buyInPaid + (m.buyInPaid || 0),
          totalExpenses: sum.totalExpenses + (m.totalExpenses || 0),
        }),
        { buyInPaid: 0, totalExpenses: 0 }
      );
      const groupPrizeWon = group.hasSeries
        ? activeMembers[0]?.seriesPrizeWon || 0
        : activeMembers.reduce((sum, m) => sum + (m.prizeWon || 0), 0);
      return {
        prizeWon: acc.prizeWon + groupPrizeWon,
        buyInPaid: acc.buyInPaid + legTotals.buyInPaid,
        totalExpenses: acc.totalExpenses + legTotals.totalExpenses,
      };
    },
    { prizeWon: 0, buyInPaid: 0, totalExpenses: 0 }
  );
  const festivalExpensesTotal = festivalExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  globalSummary.totalExpenses += festivalExpensesTotal;
  const globalNet = globalSummary.prizeWon - globalSummary.buyInPaid - globalSummary.totalExpenses;
  const hasAnyFinancialData =
    festivalExpenses.length > 0 ||
    activeParticipations.some(
      (p) => p.prizeWon != null || p.seriesPrizeWon != null || p.buyInPaid != null || p.totalExpenses != null
    );

  // Festivals proposés pour ajouter des frais : ceux où je suis inscrit (hors annulés),
  // le plus récent d'abord.
  const expenseFestivals = (() => {
    const map = new Map();
    activeParticipations.forEach((p) => {
      if (!p.festivalId) return;
      const d = dateOf(p);
      const prev = map.get(p.festivalId);
      if (!prev || d > prev.lastDate) map.set(p.festivalId, { id: p.festivalId, title: p.festivalTitle, lastDate: d });
    });
    return Array.from(map.values()).sort((a, b) => b.lastDate.localeCompare(a.lastDate));
  })();

  // Rendu d'une étape (une participation) : en-tête cliquable + contenu
  // déplié (saisie du résultat + pièces jointes). Utilisé aussi bien pour
  // un tournoi indépendant que pour chaque étape d'une série regroupée.
  const renderLeg = (p, { nested, group }) => {
    const statusInfo = STATUS_LABELS[p.status] || STATUS_LABELS.REGISTERED;
    const draft = resultDrafts[p.id] || { finishPosition: '', prizeWon: '', entriesCount: '', buyInPaid: '', totalEntries: '' };
    const isExpanded = expandedId === p.id;
    // Depuis le passage au résultat de série (voir buildGroups), un day
    // membre d'une série n'a plus de finishPosition/prizeWon propres : ce
    // résultat est saisi une seule fois au niveau de la série (voir
    // renderSeries), pas répété sur chaque day.
    const belongsToSeries = !!p.seriesId;
    const hasResult = !belongsToSeries && (p.finishPosition != null || p.prizeWon != null);
    const hasNet = p.netResult != null;
    // Un day comme un Final Day atteint sans nouvelle inscription n'a pas de
    // buy-in propre (voir Tournament.hasOwnBuyIn, V19) : les champs "Mes
    // entrées"/"Buy-in payé" et le bouton "Enregistrer" n'y servent à rien
    // et sont masqués pour ce day. Ne s'applique qu'aux days membres d'une
    // série : un tournoi indépendant est de fait toute la "série", il a
    // forcément sa propre inscription.
    const hideBuyInFields = belongsToSeries && p.hasOwnBuyIn === false;
    // Buy-in payé calculé (lecture seule) tant que le montant du tournoi et
    // les entrées sont connus, sauf si l'utilisateur est passé en saisie manuelle.
    const computedBuyIn = computeBuyInPaid(p.tournamentBuyInAmount, draft.entriesCount);
    const buyInIsManual = manualBuyInIds.has(p.id);
    const buyInIsAuto = computedBuyIn != null && !buyInIsManual;

    return (
      <div key={p.id} className={nested ? '' : 'bg-white rounded-lg shadow-lg overflow-hidden'}>
        <button
          onClick={() => toggleExpand(p)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition text-left"
        >
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-gray-800">{p.tournamentTitle}</h3>
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusInfo.className}`}>
                {statusInfo.label}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {[formatDay(dateOf(p)), !nested && p.festivalTitle].filter(Boolean).join(' · ')}
              {hasResult && (
                <span className={nested ? 'text-gray-700' : 'ml-2 text-gray-700'}>
                  {!nested && '— '}
                  {p.finishPosition ? `${p.finishPosition}e place` : 'résultat saisi'}
                  {p.prizeWon != null && ` · ${formatEuros(p.prizeWon)}`}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {hasNet && (
              <span className={`text-sm font-semibold ${p.netResult >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {p.netResult >= 0 ? '+' : ''}
                {formatEuros(p.netResult)}
              </span>
            )}
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </button>

        {group && renderQuickStrip(group)}

        {isExpanded && (
          <div className="border-t border-gray-100 p-4 space-y-6">
            {/* Saisie du résultat - masquée si ce day n'a pas de buy-in
                propre (ex: Final Day atteint sans nouvelle inscription),
                voir Tournament.hasOwnBuyIn */}
            {hideBuyInFields ? (
              <p className="text-xs text-gray-400">
                Ce day ne dispose pas d'une inscription propre (qualification depuis un day
                précédent) : rien à saisir ici.
              </p>
            ) : (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <Trophy size={16} /> {belongsToSeries ? 'Mes entrées sur ce day' : 'Mon résultat'}
              </h4>
              <div className="flex flex-wrap gap-3 items-end">
                {!belongsToSeries && (
                  <>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Position finale</label>
                      <input
                        type="number"
                        min="1"
                        value={draft.finishPosition}
                        onChange={(e) => handleDraftChange(p.id, 'finishPosition', e.target.value)}
                        placeholder="Ex: 3"
                        className="w-24 px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Cashout (€)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={draft.prizeWon}
                        onChange={(e) => handleDraftChange(p.id, 'prizeWon', e.target.value)}
                        placeholder="Ex: 500"
                        className="w-28 px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Entrées totales</label>
                      <input
                        type="number"
                        min="0"
                        value={draft.totalEntries}
                        onChange={(e) => handleDraftChange(p.id, 'totalEntries', e.target.value)}
                        placeholder="Ex: 450"
                        className="w-28 px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Mes entrées</label>
                  <input
                    type="number"
                    min="1"
                    value={draft.entriesCount}
                    onChange={(e) => handleDraftChange(p.id, 'entriesCount', e.target.value)}
                    placeholder="Ex: 2"
                    className="w-24 px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Buy-in payé (€)</label>
                  {buyInIsAuto ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={computedBuyIn}
                        title={`${draft.entriesCount} entrée(s) × ${p.tournamentBuyInAmount} €`}
                        className="w-28 px-2 py-1.5 border border-gray-200 bg-gray-50 text-gray-700 rounded"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setManualBuyInIds((prev) => new Set(prev).add(p.id))
                        }
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Modifier
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={draft.buyInPaid}
                        onChange={(e) => handleDraftChange(p.id, 'buyInPaid', e.target.value)}
                        placeholder="Ex: 60"
                        className="w-28 px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {buyInIsManual && computedBuyIn != null && (
                        <button
                          type="button"
                          onClick={() => {
                            setManualBuyInIds((prev) => {
                              const next = new Set(prev);
                              next.delete(p.id);
                              return next;
                            });
                          }}
                          className="text-xs text-blue-600 hover:underline"
                          title={`Revenir au calcul : ${computedBuyIn} €`}
                        >
                          Calcul auto
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleSaveResult(p)}
                  disabled={busyId === p.id}
                  className="bg-purple-600 text-white px-4 py-1.5 rounded-lg hover:bg-purple-700 transition disabled:opacity-50 flex items-center gap-1"
                >
                  {busyId === p.id ? <Loader size={16} className="animate-spin" /> : <Trophy size={16} />}
                  Enregistrer
                </button>
              </div>
              {nested && (
                <p className="text-xs text-gray-400 mt-2">
                  Si cette étape n'a pas de buy-in propre (ex: Final Day atteint sans frais
                  supplémentaire), laisse "Buy-in payé" vide pour ne pas fausser le bilan de la série.
                  Ta position finale et ton cashout se saisissent une seule fois, pour la série
                  entière (voir ci-dessus).
                </p>
              )}
              {hasNet && (
                <p className="text-xs text-gray-500 mt-2">
                  Bilan : {formatEuros(p.prizeWon || 0)} (gain) − {formatEuros(p.buyInPaid || 0)} (buy-in) − {formatEuros(p.totalExpenses || 0)} (frais) ={' '}
                  <span className={`font-semibold ${p.netResult >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {p.netResult >= 0 ? '+' : ''}
                    {formatEuros(p.netResult)}
                  </span>
                </p>
              )}
            </div>
            )}

            {/* Pièces jointes */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <Paperclip size={16} /> Justificatifs de frais
              </h4>

              {attachmentsLoading[p.id] ? (
                <p className="text-sm text-gray-400">Chargement...</p>
              ) : (
                <ul className="space-y-1 mb-3">
                  {(attachments[p.id] || []).length === 0 && (
                    <li className="text-sm text-gray-400">Aucun justificatif ajouté</li>
                  )}
                  {(attachments[p.id] || []).map((a) => (
                    <li
                      key={a.id}
                      className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 text-sm"
                    >
                      <span className="truncate text-gray-700">
                        {a.fileName}
                        {a.amount != null && (
                          <span className="text-gray-500"> · {formatEuros(a.amount)}</span>
                        )}
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => setViewing(a)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Ouvrir"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteAttachment(p.id, a.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={pendingAmounts[p.id] || ''}
                  onChange={(e) => setPendingAmounts((prev) => ({ ...prev, [p.id]: e.target.value }))}
                  placeholder="Montant (€, optionnel)"
                  className="w-40 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <label className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 cursor-pointer">
                  {uploadingId === p.id ? (
                    <Loader size={16} className="animate-spin" />
                  ) : (
                    <Upload size={16} />
                  )}
                  Ajouter une photo ou un PDF
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    className="hidden"
                    disabled={uploadingId === p.id}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      handleUpload(p.id, file);
                      e.target.value = '';
                  }}
                />
              </label>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Rendu d'une série multi-jours (plusieurs participations partageant la
  // même racine) : une carte unique, avec un bilan calculé une seule fois
  // pour tout le groupe, et chaque étape repliable individuellement.
  const renderSeries = (group) => {
    const activeMembers = group.members.filter((m) => m.status !== 'CANCELLED');
    // Le gain ne vient plus des days (prizeWon y est désormais toujours
    // null) mais du résultat de série (seriesPrizeWon, identique sur
    // chaque membre) - seul le buy-in/les frais restent cumulés par day.
    const seriesPrizeWon = activeMembers[0]?.seriesPrizeWon ?? null;
    const seriesTotalEntries = activeMembers[0]?.seriesTotalEntries ?? null;
    const legTotals = activeMembers.reduce(
      (acc, m) => ({
        buyInPaid: acc.buyInPaid + (m.buyInPaid || 0),
        totalExpenses: acc.totalExpenses + (m.totalExpenses || 0),
      }),
      { buyInPaid: 0, totalExpenses: 0 }
    );
    const groupNet = (seriesPrizeWon || 0) - legTotals.buyInPaid - legTotals.totalExpenses;
    const groupHasFinancialData =
      seriesPrizeWon != null || legTotals.buyInPaid !== 0 || legTotals.totalExpenses !== 0;
    const legsWithBuyIn = activeMembers.filter((m) => m.buyInPaid != null && Number(m.buyInPaid) !== 0).length;

    const draft = seriesResultDrafts[group.key] || { finishPosition: '', prizeWon: '', totalEntries: '' };

    return (
      <div key={`group-${group.key}`} className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-purple-100">
        <div className="p-4 bg-purple-50 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-bold text-gray-800 flex items-center gap-2 flex-wrap">
              <Layers size={18} className="text-purple-600" />
              {group.title}
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-purple-100 text-purple-800">
                {group.isSeries ? 'Série multi-jours' : 'Série'}
              </span>
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {group.firstDate && (
                <>
                  {group.lastDate && group.lastDate !== group.firstDate
                    ? `${formatDay(group.firstDate)} → ${formatDay(group.lastDate)}`
                    : formatDay(group.firstDate)}
                  {' · '}
                </>
              )}
              {group.festivalTitle}
              {seriesTotalEntries != null && ` · ${seriesTotalEntries} entrées (déclaré par toi)`}
            </p>
          </div>
          {groupHasFinancialData && (
            <div className="text-sm text-right">
              <div className="text-gray-500">Bilan de la série</div>
              <span className={`font-bold ${groupNet >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {groupNet >= 0 ? '+' : ''}
                {formatEuros(groupNet)}
              </span>
            </div>
          )}
        </div>

        {renderQuickStrip(group)}

        {/* Résultat final de la série : une seule position et un seul
            cashout pour l'ensemble, celui du jour où le joueur est sorti
            (ou du dernier day atteint) - pas un par day (voir SeriesResult
            côté backend). */}
        <div className="px-4 py-3 border-t border-purple-100 bg-purple-50/40">
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
            <Trophy size={16} /> Résultat final de la série
          </h4>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Position finale</label>
              <input
                type="number"
                min="1"
                value={draft.finishPosition}
                onChange={(e) => handleSeriesDraftChange(group.key, 'finishPosition', e.target.value)}
                placeholder="Ex: 3"
                className="w-24 px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Cashout (€)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={draft.prizeWon}
                onChange={(e) => handleSeriesDraftChange(group.key, 'prizeWon', e.target.value)}
                placeholder="Ex: 500"
                className="w-28 px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Entrées totales</label>
              <input
                type="number"
                min="0"
                value={draft.totalEntries}
                onChange={(e) => handleSeriesDraftChange(group.key, 'totalEntries', e.target.value)}
                placeholder="Ex: 450"
                className="w-28 px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={() => handleSaveSeriesResult(group)}
              disabled={busySeriesId === group.key}
              className="bg-purple-600 text-white px-4 py-1.5 rounded-lg hover:bg-purple-700 transition disabled:opacity-50 flex items-center gap-1"
            >
              {busySeriesId === group.key ? <Loader size={16} className="animate-spin" /> : <Trophy size={16} />}
              Enregistrer
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Laisse "Position finale" vide si tu n'es pas arrivé dans l'argent (ITM) sur cette série.
            "Entrées totales" t'est propre, comme ta position et ton cashout : chaque participant
            renseigne sa propre valeur.
          </p>
        </div>

        {legsWithBuyIn > 1 && (
          <div className="px-4 py-2 bg-amber-50 text-amber-800 text-xs border-t border-amber-100 flex items-center gap-2">
            <AlertTriangle size={14} className="shrink-0" />
            Un buy-in est renseigné sur {legsWithBuyIn} étapes de cette série — vérifie que ce n'est
            pas compté en double dans le bilan.
          </div>
        )}

        <div className="divide-y divide-gray-100 border-t border-gray-100">
          {group.members.map((p) => renderLeg(p, { nested: true }))}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 text-center text-gray-500">
          Chargement de mes participations...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-blue-600 mb-6 transition"
          >
            <ArrowLeft size={20} className="mr-2" />
            Retour à l'accueil
          </button>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
              <h1 className="text-3xl font-bold flex items-center">
                <Trophy className="mr-3" size={32} />
                Mes participations
              </h1>
              <p className="mt-2 text-blue-100">
                Saisis ton résultat une fois le tournoi joué, et ajoute tes justificatifs de frais
              </p>
            </div>

            {hasAnyFinancialData && (
              <div className="p-4 flex flex-wrap gap-6 items-center bg-gray-50">
                <div className="flex items-center gap-2">
                  <Wallet size={20} className="text-gray-500" />
                  <span className="text-sm font-semibold text-gray-700">Bilan global</span>
                </div>
                <div className="text-sm text-gray-600">
                  Gains : <span className="font-semibold text-gray-800">{formatEuros(globalSummary.prizeWon)}</span>
                </div>
                <div className="text-sm text-gray-600">
                  Buy-in : <span className="font-semibold text-gray-800">{formatEuros(globalSummary.buyInPaid)}</span>
                </div>
                <div className="text-sm text-gray-600">
                  Frais : <span className="font-semibold text-gray-800">{formatEuros(globalSummary.totalExpenses)}</span>
                </div>
                <div className="text-sm">
                  Net :{' '}
                  <span className={`font-bold ${globalNet >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {globalNet >= 0 ? '+' : ''}
                    {formatEuros(globalNet)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {error}
            </div>
          )}

          {participations.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500">
                Tu n'es inscrit à aucun tournoi pour l'instant. Rends-toi sur le calendrier pour t'inscrire.
              </p>
            </div>
          ) : (
            <>
              <FestivalExpensesCard
                festivals={expenseFestivals}
                expenses={festivalExpenses}
                onCreated={(created) => setFestivalExpenses((prev) => [created, ...prev])}
                onDeleted={(id) => setFestivalExpenses((prev) => prev.filter((e) => e.id !== id))}
                onUpdated={(updated) =>
                  setFestivalExpenses((prev) => prev.map((e) => (e.id === updated.id ? updated : e)))
                }
              />

              {/* Filtres : onglets + recherche + année */}
              <div className="bg-white rounded-lg shadow p-3 mb-4 space-y-3">
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {TABS.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTab(t.id)}
                      className={`whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-medium transition flex items-center gap-1.5 ${
                        tab === t.id
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t.label}
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded-full ${
                          tab === t.id
                            ? 'bg-white/25 text-white'
                            : t.id === 'todo' && counts.todo > 0
                            ? 'bg-amber-200 text-amber-900'
                            : 'bg-white text-gray-600'
                        }`}
                      >
                        {counts[t.id]}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Rechercher un tournoi ou un festival"
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {availableYears.length > 1 && (
                    <select
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">Toutes les années</option>
                      {availableYears.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {tab !== 'todo' && counts.todo > 0 && (
                <button
                  onClick={() => setTab('todo')}
                  className="w-full mb-4 text-left bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm flex items-center gap-2 hover:bg-amber-100 transition"
                >
                  <AlertTriangle size={16} className="shrink-0" />
                  <span>
                    <strong>{counts.todo}</strong> résultat{counts.todo > 1 ? 's' : ''} à saisir — voir la liste
                  </span>
                </button>
              )}

              {filteredGroups.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                  {tab === 'todo' && !search && year === 'all'
                    ? 'Tout est à jour : aucun résultat en attente.'
                    : 'Aucune participation ne correspond à ces filtres.'}
                </div>
              ) : (
                <div className="space-y-4">
                  {visibleGroups.map((group) =>
                    group.hasSeries ? renderSeries(group) : renderLeg(group.members[0], { nested: false, group })
                  )}
                </div>
              )}

              {filteredGroups.length > visibleCount && (
                <button
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="w-full mt-4 bg-white border border-gray-300 text-gray-700 rounded-lg py-3 font-medium hover:bg-gray-50 transition"
                >
                  Afficher plus ({filteredGroups.length - visibleCount} restantes)
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {viewing && (
        <AttachmentViewer
          attachment={viewing}
          loadUrl={participationsAPI.downloadAttachment}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
};

export default MyParticipationsPage;
